import React, { useState, useEffect, useMemo } from 'react';
import { 
  Button, 
  Alert, 
  Box, 
  Grid, 
  Typography, 
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { agregarTurno, obtenerDetallesServicio, parseRequierePersonas } from '../data/FirestoreCuadrantes';
import { fetchVigilantesPorServicio } from '../../admin/firestoreService';
import { 
  buscarServicioFreematica,
  obtenerTagPersona,
  extraerDatosLocalizacion,
  crearMarcajeTurno,
  crearMarcajesTurnoLote
} from '../services/ApiMarcajes';
import './CreateTramo.css';

// Función helper para formatear el nombre del vigilante
const formatearVigilante = (vigilante) => {
  const nombreCompleto = `${vigilante.nombre || ''} ${vigilante.apellidos || ''}`.trim();
  const persona = vigilante.persona || '';
  
  if (persona && nombreCompleto) {
    return `${persona} - ${nombreCompleto}`;
  } else if (persona) {
    return persona;
  } else if (nombreCompleto) {
    return nombreCompleto;
  } else {
    return vigilante.id || 'Sin nombre';
  }
};

const CreateTramo = ({ onClose, cuadranteId, currentService, servicioId, onTramoCreado, open = true }) => {
  // Datos para el tramo (sin vigilante)
  const [turno, setTurno] = useState('');
  const [mesInicio, setMesInicio] = useState('');
  const [mesFin, setMesFin] = useState('');
  const [diasSeleccionados, setDiasSeleccionados] = useState({});
  const [horaInicio, setHoraInicio] = useState('08:00');
  const [horaFin, setHoraFin] = useState('16:00');
  const [arcos, setArcos] = useState(false);
  const [manejoExplosivos, setManejoExplosivos] = useState(false);
  const [peligrosidad, setPeligrosidad] = useState(false);
  const [conArma, setConArma] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [isNightShift, setIsNightShift] = useState(false);
  
  // Asignación opcional de usuario al tramo (todos los vigilantes del servicio + filtro por persona)
  const [asignarUsuario, setAsignarUsuario] = useState(false);
  const [vigilanteSeleccionado, setVigilanteSeleccionado] = useState('');
  const [vigilantes, setVigilantes] = useState([]);
  const [busquedaVigilante, setBusquedaVigilante] = useState('');
  const [loadingVigilantes, setLoadingVigilantes] = useState(false);
  
  // Estados de carga y errores
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState(false);
  const [detallesServicio, setDetallesServicio] = useState(null);
  const [loading, setLoading] = useState(true);

  // Datos de Freematica
  const [empresa, setEmpresa] = useState('0001');
  
  // No inicializar mes por defecto - el usuario debe seleccionarlo manualmente
  
  // Estado para mantener el patrón de días seleccionados
  const [patronDiasSeleccionados, setPatronDiasSeleccionados] = useState({});

  // Efecto para inicializar los días seleccionados cuando cambia el mes
  useEffect(() => {
    if (mesInicio) {
      const [year, month] = mesInicio.split('-');
      const lastDay = new Date(year, month, 0).getDate();
      const newDiasSeleccionados = {};

      // Aplicar el patrón de días seleccionados al mes actual
      for (let i = 1; i <= lastDay; i++) {
        newDiasSeleccionados[i] = patronDiasSeleccionados[i] || false;
      }

      setDiasSeleccionados(newDiasSeleccionados);
    }
  }, [mesInicio, patronDiasSeleccionados]);

  // Efecto para detectar automáticamente turnos nocturnos cuando cambian las horas
  useEffect(() => {
    detectNightShift();
  }, [horaInicio, horaFin]);

  // Misma carga que AsignarVigilante: TODOS los vigilantes del servicio (fetchVigilantesPorServicio) y enriquecer persona
  useEffect(() => {
    const cargarVigilantes = async () => {
      if (!open || !currentService?.id) {
        setVigilantes([]);
        return;
      }
      try {
        setLoadingVigilantes(true);
        const vigilantesData = await fetchVigilantesPorServicio(currentService);
        if (vigilantesData && vigilantesData.length > 0) {
          const vigilantesValidados = vigilantesData.map((v) => {
            const persona = v.persona || (v.nombre || v.apellidos ? `${v.nombre || ''} ${v.apellidos || ''}`.trim() : '');
            return { ...v, persona: persona || v.persona };
          });
          setVigilantes(vigilantesValidados);
        } else {
          setVigilantes([]);
        }
      } catch (err) {
        console.warn('Error al cargar vigilantes en CreateTramo:', err);
        setVigilantes([]);
      } finally {
        setLoadingVigilantes(false);
      }
    };
    cargarVigilantes();
  }, [open, currentService]);

  // Filtrar vigilantes por nombre, apellidos o código de persona (como en otros apartados)
  const vigilantesFiltrados = useMemo(() => {
    if (!busquedaVigilante.trim()) return vigilantes;
    const normalizar = (s) => String(s ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const busqueda = normalizar(busquedaVigilante);
    return vigilantes.filter((v) => {
      const nombre = normalizar(`${v.nombre || ''} ${v.apellidos || ''}`);
      const persona = normalizar(v.persona || '');
      const nif = normalizar(v.nif || '');
      return nombre.includes(busqueda) || persona.includes(busqueda) || nif.includes(busqueda);
    });
  }, [vigilantes, busquedaVigilante]);

  // Cargar datos del servicio
  useEffect(() => {
    const cargarDetallesServicio = async () => {
      try {
        setLoading(true);
        const servicioIdToUse = Array.isArray(servicioId) ? servicioId[0] : servicioId;
        
        if (servicioIdToUse) {
          const detalles = await obtenerDetallesServicio(servicioIdToUse, currentService);
          setDetallesServicio(detalles);
          
          // Establecer la empresa automáticamente si existe en los detalles
          if (detalles && detalles.empresa) {
            setEmpresa(detalles.empresa);
          }
        }
      } catch (error) {
        console.warn('Error al cargar detalles del servicio:', error);
        setError('Error al cargar detalles del servicio: ' + error.message);
      } finally {
        setLoading(false);
      }
    };
    
    cargarDetallesServicio();
  }, [servicioId, currentService]);

  // Función para obtener los días en el mes seleccionado, preparados para el calendario
  const getDaysInMonth = () => {
    if (!mesInicio) return [];
    const [year, month] = mesInicio.split('-');
    const date = new Date(year, month - 1, 1);
    const days = [];
    const lastDay = new Date(year, month, 0).getDate();

    // Obtener el día de la semana del primer día (0 = Domingo, 1 = Lunes, etc.)
    const firstDayOfWeek = date.getDay();

    // Añadir días vacíos al principio para alinear el calendario
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }

    // Añadir los días del mes
    for (let i = 1; i <= lastDay; i++) {
      days.push(i);
    }

    return days;
  };

  // Función para alternar la selección de un día
  const toggleDia = (dia) => {
    if (dia === null) return; // Ignorar espacios vacíos
    
    const nuevoValor = !diasSeleccionados[dia];
    
    // Actualizar el patrón global de días seleccionados
    setPatronDiasSeleccionados(prev => ({
      ...prev,
      [dia]: nuevoValor
    }));
    
    // Actualizar los días del mes actual
    setDiasSeleccionados(prev => ({
      ...prev,
      [dia]: nuevoValor
    }));
  };

  // Contar los días seleccionados
  const contarDiasSeleccionados = () => {
    return Object.values(diasSeleccionados).filter(Boolean).length;
  };

  // Obtener los días del patrón seleccionados
  const obtenerDiasPatronSeleccionados = () => {
    return Object.entries(patronDiasSeleccionados)
      .filter(([_, seleccionado]) => seleccionado)
      .map(([dia, _]) => parseInt(dia))
      .sort((a, b) => a - b);
  };

  // Manejadores de cambios
  const handleTurnoChange = (event) => {
    setTurno(event.target.value);
  };

  const handleMesInicioChange = (event) => {
    setMesInicio(event.target.value);
  };

  const handleMesFinChange = (event) => {
    setMesFin(event.target.value);
  };

  const handleHoraInicioChange = (event) => {
    setHoraInicio(event.target.value);
    // La detección de turno nocturno se hace en el useEffect
  };

  const handleHoraFinChange = (event) => {
    setHoraFin(event.target.value);
    // La detección de turno nocturno se hace en el useEffect
  };

  // Función para detectar automáticamente turnos nocturnos
  const detectNightShift = () => {
    if (!horaInicio || !horaFin) return;
    
    // Convertir las horas a minutos para una comparación más sencilla
    const [inicioHoras, inicioMinutos] = horaInicio.split(':').map(Number);
    const [finHoras, finMinutos] = horaFin.split(':').map(Number);
    
    const inicioTotal = inicioHoras * 60 + inicioMinutos;
    const finTotal = finHoras * 60 + finMinutos;
    
    // Si la hora de fin es menor que la hora de inicio, se trata de un turno nocturno
    setIsNightShift(finTotal < inicioTotal);
  };

  const handleEmpresaChange = (event) => {
    setEmpresa(event.target.value);
  };

  const handleCheckboxChange = (setter) => () => {
    setter(prev => !prev);
  };

  // Función para seleccionar todos los días
  const seleccionarTodos = () => {
    const nuevosDias = { ...diasSeleccionados };
    const nuevoPatron = { ...patronDiasSeleccionados };
    
    Object.keys(nuevosDias).forEach(dia => {
      nuevosDias[dia] = true;
      nuevoPatron[dia] = true;
    });
    
    setDiasSeleccionados(nuevosDias);
    setPatronDiasSeleccionados(nuevoPatron);
  };

  // Función para deseleccionar todos los días
  const deseleccionarTodos = () => {
    const nuevosDias = { ...diasSeleccionados };
    const nuevoPatron = { ...patronDiasSeleccionados };
    
    Object.keys(nuevosDias).forEach(dia => {
      nuevosDias[dia] = false;
      nuevoPatron[dia] = false;
    });
    
    setDiasSeleccionados(nuevosDias);
    setPatronDiasSeleccionados(nuevoPatron);
  };

  // Función para limpiar el patrón de días
  const limpiarPatron = () => {
    setPatronDiasSeleccionados({});
  };

     // Función para limpiar el rango de meses
   const limpiarRangoMeses = () => {
     setMesInicio('');
     setMesFin('');
   };

  // Función para crear un turno para una fecha específica
  const crearTurnoParaFecha = async (fecha) => {
    // No se necesita un vigilante para crear un turno sin asignación
    // La lógica de asignación externa se maneja en el backend

    const turnoData = {
      fecha: fecha,
      inicioTurno: horaInicio,
      finTurno: horaFin,
      turno: turno,
      arcos,
      manejoExplosivos,
      peligrosidad,
      conArma,
      observaciones,
      servicioId: servicioId, // Incluir el ID del servicio
      nextDayEnd: isNightShift // Usar el valor detectado automáticamente
    };

    // Usar la función mejorada que ya incluye la integración con Freematica
    return await agregarTurno(cuadranteId, turnoData, currentService);
  };

  // Función principal para crear el tramo (actualizada para crear turnos sin vigilante)
const handleSubmit = async (event) => {
  event.preventDefault();
  setEnviando(true);
  setError('');
  setExito(false);

  try {
    // Validaciones básicas
    if (!mesInicio || !mesFin) {
      throw new Error('Debe seleccionar un rango de meses');
    }

    // Verificar que hay días seleccionados
    const hayDiasSeleccionados = Object.values(diasSeleccionados).some(selected => selected);
    if (!hayDiasSeleccionados) {
      throw new Error('Debe seleccionar al menos un día');
    }

    // Recopilar todas las fechas seleccionadas para el rango de meses
    const fechasSeleccionadas = [];

    // Generar todos los meses entre mesInicio y mesFin
    const mesesEnRango = [];
    const [inicioYear, inicioMonth] = mesInicio.split('-').map(Number);
    const [finYear, finMonth] = mesFin.split('-').map(Number);
    
    let currentYear = inicioYear;
    let currentMonth = inicioMonth;
    
    while (currentYear < finYear || (currentYear === finYear && currentMonth <= finMonth)) {
      const mesValue = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
      mesesEnRango.push(mesValue);
      
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    // Para cada mes en el rango
    for (const mesSeleccionado of mesesEnRango) {
      const [year, month] = mesSeleccionado.split('-');
      const ultimoDiaDelMes = new Date(year, month, 0).getDate();
      
      Object.entries(diasSeleccionados).forEach(([dia, seleccionado]) => {
        if (seleccionado) {
          const diaNum = parseInt(dia);
          // Solo incluir si el día existe en este mes (evitar día 31 en febrero, etc.)
          if (diaNum <= ultimoDiaDelMes) {
            const formattedDate = [
              year,
              String(month).padStart(2, '0'),
              String(diaNum).padStart(2, '0')
            ].join('-');
            fechasSeleccionadas.push(formattedDate);
          }
        }
      });
    }
    
    // Crear los turnos para cada fecha seleccionada en la base de datos local (Firestore)
    const turnosFirestore = [];
    
    const vigilanteData = asignarUsuario && vigilanteSeleccionado
      ? vigilantes.find(v => v.id === vigilanteSeleccionado)
      : null;

    const numPersonas = parseRequierePersonas(observaciones);

    for (const fecha of fechasSeleccionadas) {
      const baseTurnoData = {
        fecha: fecha,
        inicioTurno: horaInicio,
        finTurno: horaFin,
        turno: turno,
        arcos,
        manejoExplosivos,
        peligrosidad,
        conArma,
        observaciones,
        servicioId: servicioId,
        nextDayEnd: isNightShift,
        fechaCreacion: new Date().toISOString()
      };

      for (let i = 0; i < numPersonas; i++) {
        const turnoData = { ...baseTurnoData };
        if (vigilanteData && i === 0) {
          turnoData.vigilante = vigilanteData.persona;
          turnoData.vigilanteId = vigilanteData.id;
          turnoData.personaId = vigilanteData.persona;
          turnoData.sinAsignar = false;
        } else {
          turnoData.sinAsignar = true;
        }
        turnosFirestore.push(turnoData);
      }
    }
    
    // 1. Guardar todos los turnos en Firestore (sin enviar a Freematica)
    console.log(`Guardando ${turnosFirestore.length} turnos sin asignar en Firestore`);
    const resultadosFirestore = await Promise.all(
      turnosFirestore.map(turnoData => 
        agregarTurno(cuadranteId, turnoData, currentService, false) // false = no enviar a Freematica
      )
    );
    
    setExito(true);
    if (onTramoCreado) {
      await onTramoCreado();
    }
    setTimeout(() => {
      onClose(); // Cerrar el modal después de un breve tiempo
    }, 2000);
    
  } catch (error) {
    console.warn('Error al crear el tramo:', error);
    setError(error.message || 'Error al crear el tramo');
  } finally {
    setEnviando(false);
  }
};

  // Mostrar loading state
  if (loading) {
    return (
      <div className="create-tramo-container">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography ml={2}>Cargando datos...</Typography>
        </Box>
      </div>
    );
  }

           // Obtener nombres del rango de meses para mostrar
    const getNombresRangoMeses = () => {
      if (!mesInicio || !mesFin) return '';
      
      const [inicioYear, inicioMonth] = mesInicio.split('-');
      const [finYear, finMonth] = mesFin.split('-');
      const inicioDate = new Date(inicioYear, inicioMonth - 1, 1);
      const finDate = new Date(finYear, finMonth - 1, 1);
      
      if (mesInicio === mesFin) {
        return inicioDate.toLocaleString('es-ES', { month: 'long' }) + ' ' + inicioYear;
      }
      
      return `${inicioDate.toLocaleString('es-ES', { month: 'short' })} ${inicioYear} - ${finDate.toLocaleString('es-ES', { month: 'short' })} ${finYear}`;
    };

  return (
    <div className="create-tramo-container">
      <Typography variant="h5" component="h2" gutterBottom>
        Crear Tramo de Turnos
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: '16px' }}>
          {error}
        </Alert>
      )}

      {exito && (
        <Alert severity="success" style={{ marginBottom: '16px' }}>
          Tramo creado correctamente
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Asignación opcional de usuario */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={asignarUsuario}
                  onChange={(e) => {
                    setAsignarUsuario(e.target.checked);
                    if (!e.target.checked) setVigilanteSeleccionado('');
                  }}
                />
              }
              label="Asignar usuario al tramo"
            />
            {asignarUsuario && (
              <Box mt={1} ml={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Filtrar por persona"
                  placeholder="Nombre, apellido o código de persona..."
                  value={busquedaVigilante}
                  onChange={(e) => setBusquedaVigilante(e.target.value)}
                  sx={{ mb: 1, minWidth: 280 }}
                />
                <FormControl fullWidth size="small" sx={{ minWidth: 280 }}>
                  <InputLabel>Usuario</InputLabel>
                  <Select
                    value={vigilanteSeleccionado}
                    onChange={(e) => setVigilanteSeleccionado(e.target.value)}
                    label="Usuario"
                    disabled={loadingVigilantes}
                    MenuProps={{
                      disablePortal: false,
                      sx: { zIndex: 10001 },
                      PaperProps: { sx: { zIndex: 10001 } },
                      slotProps: { root: { style: { zIndex: 10001 } } }
                    }}
                  >
                    <MenuItem value="">
                      <em>Sin asignar</em>
                    </MenuItem>
                    {vigilantesFiltrados.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {formatearVigilante(v)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {vigilantesFiltrados.length} usuario(s){busquedaVigilante.trim() ? ' (filtrados)' : ''}
                </Typography>
                {loadingVigilantes && <CircularProgress size={20} sx={{ mt: 1 }} />}
              </Box>
            )}
          </Grid>

          {/* Sección de usuario y turno */}
          <Grid item xs={12} md={6}>
            <label className="form-label">
              Turno:
              <input
                type="text"
                value={turno}
                onChange={handleTurnoChange}
                className="form-input"
                placeholder="Ej: Mañana"
              />
            </label>
          </Grid>

                    {/* Sección de selección de rango de meses y días */}
          <Grid item xs={12}>
            <Box mb={2}>
              <Typography variant="subtitle1" gutterBottom>
                Seleccionar Rango de Meses:
              </Typography>
              <Box display="flex" gap={2} mb={2} alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Mes de inicio:
                  </Typography>
                  <input
                    type="month"
                    value={mesInicio}
                    onChange={handleMesInicioChange}
                    className="form-input"
                    required
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Mes de fin:
                  </Typography>
                  <input
                    type="month"
                    value={mesFin}
                    onChange={handleMesFinChange}
                    className="form-input"
                    required
                  />
                </Box>
              </Box>
              
              {mesInicio && mesFin && (
                <Box p={1} bgcolor="#e8f4f8" borderRadius={1}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="primary.main">
                      <strong>Rango seleccionado:</strong> {getNombresRangoMeses()}
                    </Typography>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      color="error"
                      onClick={limpiarRangoMeses}
                    >
                      Resetear
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
            
            <Typography variant="body2" color="text.secondary" mt={1}>
              Selecciona un rango de meses. Los días seleccionados se aplicarán a todos los meses del rango.
            </Typography>
            
            {mesInicio && mesFin && (
              <div className="calendar-container">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {getNombresRangoMeses()} - Seleccionar días:
                  </Typography>
                  <Box>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      color="primary" 
                      onClick={seleccionarTodos}
                      sx={{ mr: 1 }}
                    >
                      Seleccionar todos
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      color="secondary" 
                      onClick={deseleccionarTodos}
                    >
                      Deseleccionar todos
                    </Button>
                  </Box>
                </Box>
                
                {/* Información del patrón de días */}
                {obtenerDiasPatronSeleccionados().length > 0 && (
                  <Box mb={2} p={1} bgcolor="#e8f5e8" borderRadius={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="success.main">
                        <strong>Patrón de días:</strong> Los días {obtenerDiasPatronSeleccionados().join(', ')} se mantendrán seleccionados para otros meses.
                      </Typography>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        color="error"
                        onClick={limpiarPatron}
                      >
                        Limpiar patrón
                      </Button>
                    </Box>
                  </Box>
                )}
                
                <div className="calendar-header">
                  <div>Dom</div>
                  <div>Lun</div>
                  <div>Mar</div>
                  <div>Mié</div>
                  <div>Jue</div>
                  <div>Vie</div>
                  <div>Sáb</div>
                </div>
                <div className="calendar-grid">
                  {getDaysInMonth().map((dia, index) => (
                    <div
                      key={index}
                      className={`calendar-day ${dia === null ? 'empty' : ''} ${dia !== null && diasSeleccionados[dia] ? 'selected' : ''}`}
                      onClick={() => toggleDia(dia)}
                    >
                      {dia}
                    </div>
                  ))}
                </div>
                
                {contarDiasSeleccionados() > 0 && (
                  <div className="selected-days-summary">
                    {contarDiasSeleccionados()} día(s) seleccionado(s)
                  </div>
                )}
              </div>
            )}
          </Grid>

          {/* Sección de horas */}
          <Grid item xs={12} md={6}>
            <label className="form-label">
              Hora de inicio:
              <input
                type="time"
                value={horaInicio}
                onChange={handleHoraInicioChange}
                className="form-input"
                required
              />
            </label>
          </Grid>

          <Grid item xs={12} md={6}>
            <label className="form-label">
              Hora de fin:
              <input
                type="time"
                value={horaFin}
                onChange={handleHoraFinChange}
                className="form-input"
                required
              />
            </label>
          </Grid>

          {/* Indicador automático de turno nocturno */}
          {isNightShift && (
            <Grid item xs={12}>
              <div className="night-shift-info">
                <Box display="flex" alignItems="center">
                  <InfoIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" fontWeight="medium" color="primary.main">
                    Turno nocturno detectado: La hora de fin es anterior a la hora de inicio.
                  </Typography>
                </Box>
                <Typography variant="body2" mt={1}>
                  El sistema registrará automáticamente que la hora de salida corresponde al día siguiente.
                </Typography>
              </div>
            </Grid>
          )}

          {/* Sección de opciones */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Características del turno:
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox checked={arcos} onChange={handleCheckboxChange(setArcos)} />}
                label="Arcos"
              />
              <FormControlLabel
                control={<Checkbox checked={manejoExplosivos} onChange={handleCheckboxChange(setManejoExplosivos)} />}
                label="Manejo de Explosivos"
              />
              <FormControlLabel
                control={<Checkbox checked={peligrosidad} onChange={handleCheckboxChange(setPeligrosidad)} />}
                label="Peligrosidad"
              />
              <FormControlLabel
                control={<Checkbox checked={conArma} onChange={handleCheckboxChange(setConArma)} />}
                label="Con Arma"
              />
            </FormGroup>
          </Grid>

          {/* Sección de Freematica */}
          <Grid item xs={12}>
            <Box bgcolor="#f5f5f5" p={2} borderRadius={1} mt={2}>
              <Typography variant="subtitle1">Datos para Freematica:</Typography>
              
              <label className="form-label">
                Empresa:
                <select
                  value={empresa}
                  onChange={handleEmpresaChange}
                  className="form-select"
                >
                  <option value="0001">0001 - SEGURIDAD INTEGRAL SECOEX</option>
                  <option value="0005">0005 - PRESTACIONES AUXILIARES VALENCIANAS</option>
                  <option value="0006">0006 - SERV. Y FUNCIONES AUX. MANCHEGOS</option>
                  <option value="0008">0008 - SCU PRACOM ANDALUCIA</option>
                  <option value="0013">0013 - PRACON CENTRO DE EMPLEO MADRID</option>
                  <option value="0016">0016 - TELIMAN SERVICIOS COMPLEMENTARIOS</option>
                  <option value="0020">0020 - PRACON SERVIEXT</option>
                </select>
              </label>
              
              {detallesServicio && (
                <Box mt={2} p={1} bgcolor="#e8f4f8" borderRadius={1}>
                  <Typography variant="subtitle2">Información del Servicio:</Typography>
                  <Typography variant="body2">
                    <strong>Localización:</strong> {detallesServicio.localizacion || 'No disponible'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Contrato:</strong> {detallesServicio.contrato || 'No disponible'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Servicio:</strong> {detallesServicio.delegacion || 'No disponible'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Observaciones */}
          <Grid item xs={12}>
            <label className="form-label">
              Observaciones:
              <textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                className="form-textarea"
                rows={3}
                placeholder="Escribe 'requiere X personas' para crear X filas por día"
              />
            </label>
          </Grid>

          {/* Botones */}
          <Grid item xs={12}>
            <div className="button-container">
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={onClose}
                disabled={enviando}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                                 disabled={enviando || !mesInicio || !mesFin || contarDiasSeleccionados() === 0}
                sx={{ ml: 2 }}
              >
                {enviando ? 'Creando...' : 'Crear Tramo'}
              </Button>
            </div>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default CreateTramo;